import Lozenge from '@atlaskit/lozenge';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useMessageContext } from '@post-office/message-context';
import { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { AdControls } from '@post-office/components--ad-controls-sequoia';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { Box } from '@atlaskit/primitives';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { AssetsIcon } from './assets/assets-icon';
import { type JSX } from 'react/jsx-runtime';

interface PlacementRenderProps {
	dismiss: (messageInstanceId: string) => void;
	onItemClick?: () => void;
	onItemViewed?: () => void;
}

export type AssetsPremiumTrialItemProps = PlacementRenderProps;

const whyAmISeeingThisModalContent = {
	title: (
		<FormattedMessage
			id="post-office.jsm-assets-premium-trial-item.reasons-modal-title"
			defaultMessage="Why am I seeing this?"
			description="Title of the reasons modal explaining why the premium trial is being shown"
		/>
	),
	message: (
		<FormattedMessage
			id="post-office.jsm-assets-premium-trial-item.reasons-modal-explanation"
			defaultMessage="We think this premium feature will help you get more out of Assets. The recommendation is based on:"
			description="Explanation as to why the premium trial is being shown"
		/>
	),
	reasons: [
		<FormattedMessage
			key="reason-1"
			id="post-office.jsm-assets-premium-trial-item.reasons-modal-bullet-one"
			defaultMessage="your current product usage"
			description="First bullet point of the reasons modal"
		/>,
		<FormattedMessage
			key="reason-2"
			id="post-office.jsm-assets-premium-trial-item.reasons-modal-bullet-two"
			defaultMessage="features available in premium"
			description="Second bullet point of the reasons modal"
		/>,
	],
};

const containerAvatarStyle = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '24px',
	height: '24px',
};

export const AssetsPremiumTrialItem = ({
	dismiss,
	onItemClick,
	onItemViewed,
}: AssetsPremiumTrialItemProps): JSX.Element => {
	const { messageInstanceClicked, messageInstanceViewed } = useMessageInstanceEvent();
	const { messageInstanceId } = useMessageContext();

	useEffect(() => {
		void messageInstanceViewed({
			messageEventType: 'viewed',
		});
		onItemViewed?.();
	}, [messageInstanceViewed, onItemViewed]);

	const handleClick = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'try-premium-trial',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'react',
			},
			additionalMetadataFromFrontend: {},
		});

		// Call the parent's click handler instead of navigating
		onItemClick?.();
	}, [messageInstanceClicked, onItemClick]);

	const handleDismiss = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {},
		});

		if (messageInstanceId) {
			dismiss(messageInstanceId);
		}
	}, [dismiss, messageInstanceClicked, messageInstanceId]);

	return (
		<LinkMenuItem
			elemBefore={<AssetsIcon />}
			elemAfter={
				<>
					<Lozenge appearance="new">
						<span id="assets-premium-trial-navigation-item-lozenge-try">
							<FormattedMessage
								id="post-office.jsm-assets-premium-trial-item.lozenge-try"
								defaultMessage="Try"
								description="A lozenge to display the try button for premium trial"
							/>
						</span>
					</Lozenge>
					<Box style={containerAvatarStyle} aria-hidden="true">
						<LinkExternalIcon label="" size="small" color="currentColor" />
					</Box>
				</>
			}
			href="/jira/assets"
			target="_blank"
			onClick={handleClick}
			actionsOnHover={
				<AdControls
					iconSize="small"
					spacing="compact"
					onDismissed={handleDismiss}
					showWhyAmISeeingThis={true}
					whyAmISeeingThisModalContent={whyAmISeeingThisModalContent}
					ariaLabelledby="assets-premium-trial-navigation-item-message-title"
				/>
			}
		>
			<span id="assets-premium-trial-navigation-item-message-title">
				<FormattedMessage
					id="post-office.jsm-assets-premium-trial-item.menu-item-label"
					defaultMessage="Assets"
					description="Menu item label for the Assets premium trial"
				/>
			</span>
		</LinkMenuItem>
	);
};
